import * as React from 'react';
import { observer } from 'mobx-react';

import FilterableAttributes from '../../components/Query/Attributes/FilterableAttributes';
import { IOrderPrice, IProduct } from '../../interfaces/interfaces';
import { CurrencyFormatter as Currency } from '../../utils/utils';
import { ProductBox } from '../modules';
import Title from '../Title/Title';
import CountryService from '../../services/CountryService';
import VatService from '../../services/VatService';
import { getOrderVat } from '../../helpers/orders/getOrderVat';
import getTotalTariffFee from '../../helpers/orders/getTotalTariffFee';

import CartService from '../../services/CartService';
import { getPriceWithoutVat } from 'data-layer/src/helpers/prices/getPriceWithoutVat';
import { getPriceWithVat } from 'data-layer/src/helpers/prices/getPriceWithVat';
import { vatDescription } from '../../helpers/vatDescription';

interface IProductInfo {
  variant: GQL.IProductVariant;
  quantity: number;
  size: string | undefined;
}

interface IOrderBoxProps {
  title?: string;
  className?: string;
  type?: 'cart' | 'order';
  order?: GQL.IOrder;
  products: IProductInfo[];
  price: IOrderPrice;
  payment?: GQL.IPaymentMethod | null;
  delivery?: GQL.IDeliveryMethod | null;
}

@observer
class OrderBox extends React.Component<IOrderBoxProps> {

  public render() {
    const { language, VATrate, currency: countryCurrency, countryCode } = CountryService.GetInstance();
    const { title, className, type, price, order, products, payment, delivery } = this.props;

    const summaryVatRate = order ? getOrderVat(order) : VATrate;
    const summaryCountry = order && order.country ? order.country : null;
    const currency = order && order.currency ? order.currency : countryCurrency;
    const totalTariffFeeWithoutVat = getTotalTariffFee(products, currency, 0);
    const totalPriceWithVat = price.Total + (price.Payment || 0) + (price.Shipment || 0) + (price.DeliveryInsurance || 0) - (price.Discount || 0) + totalTariffFeeWithoutVat;

    // exclude prices withoutt VAT and add it on the end
    const totalPriceWithoutVat = (countryCode.toLowerCase() === 'us')
      ? getPriceWithoutVat(totalPriceWithVat - totalTariffFeeWithoutVat - (price.Shipment || 0), summaryVatRate) + (price.Shipment || 0) + totalTariffFeeWithoutVat
      : getPriceWithoutVat(totalPriceWithVat, summaryVatRate);

    const deliveryVATrate = (countryCode.toLowerCase() === 'us') ? 0 : summaryVatRate;
    
    return (
      <div className={`orderBox${className ? ` ${className}` : ''}`}>

        {title &&
          <Title type="shopping-cart-subtitle">
            {title}
          </Title>}
        
        <FilterableAttributes>
          {({ attributes }: any) => (
            <div className="orderBox__content">
              {products.map((product, i) => {
                return (
                  <div key={i} className="orderBox__item">
                    <ProductBox
                      orderItem={product}
                      variant={product.variant}
                      quantity={product.quantity}
                      boxType={type || 'cart'}
                      attributes={attributes.attributes || []}
                      currency={currency}
                    />
                  </div>
                );
              })}

              {totalTariffFeeWithoutVat && (<div className="orderBox__info">
                <div className="orderBox__infoItem">
                  <div className="orderBox__label">
                    U.S. Reciprocal Import Tariff
                  </div>
                  <div className="orderBox__price d-flex align-items-center">
                    <div>
                      <Currency price={getPriceWithoutVat(totalTariffFeeWithoutVat, 0)} decimals={2} currency={currency} />
                    </div>
                  </div>
                </div>
              </div>) || ''}

              <div className="orderBox__info">
                <div className="orderBox__infoItem">
                  <div className="orderBox__label">
                    Payment
                    {payment && 
                      <span> {payment && payment.name && payment.name[language || 'en']}</span>}
                  </div>
                  <div className="orderBox__price d-flex align-items-center">
                    {price.Payment !== null && price.Payment !== undefined &&
                      <div>
                        <Currency price={getPriceWithoutVat(price.Payment, summaryVatRate)} decimals={2} currency={currency} />
                        {summaryVatRate > 0 && price.Payment > 0 &&
                          <div>
                            <span className="withoutVat">
                              <Currency price={price.Payment} currency={currency} /> incl. {vatDescription()}
                            </span>
                          </div>}
                      </div>}
                    {price.Payment === null &&
                      <span>Not set</span>}
                  </div>
                </div>

                <div className="orderBox__infoItem">
                  <div className="orderBox__label">
                    Shipping
                    {delivery && 
                      <span> {delivery.name[language || 'en']}</span>}
                  </div>
                  <div className="orderBox__price d-flex align-items-center">
                    {price.Shipment !== null && price.Shipment !== undefined &&
                      <div>
                        <Currency price={getPriceWithoutVat(price.Shipment, deliveryVATrate)} decimals={2} currency={currency} />
                        {summaryVatRate > 0 && price.Shipment > 0 &&
                          countryCode != 'us' && <div>
                            <span className="withoutVat">
                              <Currency price={price.Shipment} currency={currency} /> incl. {vatDescription()}
                            </span>
                          </div>}
                      </div>}
                    {price.Shipment === null &&
                      <span>{delivery ? (delivery.unknownPriceLabel || {})[language] : ''}</span>}
                    {price.Shipment === undefined &&
                      <span>Not set</span>}
                  </div>
                </div>

                {CartService.GetInstance().individualCustomerVoucherDiscount &&
                <div className="orderBox__infoItem">
                  <div className="orderBox__label">
                    {CartService.GetInstance().voucherTitle}
                  </div>
                  <div className="orderBox__price d-flex align-items-center">
                    <div>
                      <Currency
                          price={-CartService.GetInstance().individualCustomerVoucherDiscountWithoutVat}
                          decimals={2}
                          currency={currency}
                        />
                      {summaryVatRate > 0 && <div>
                        <span className="withoutVat">
                         <Currency
                          price={-CartService.GetInstance().individualCustomerVoucherDiscount}
                          currency={currency}
                        /> incl. {vatDescription()}
                        </span>
                      </div>}
                    </div>
                  </div>
                </div>                    
                    || ''}

                {price.DeliveryInsurance !== null && price.DeliveryInsurance > 0 &&
                  <div className="orderBox__infoItem">
                    <div className="orderBox__label">
                      Shipment insurance
                    </div>
                    <div className="orderBox__price d-flex align-items-center">
                      <div>
                        <Currency
                                price={getPriceWithoutVat(price.DeliveryInsurance, summaryVatRate)}
                                decimals={2}
                                currency={order && order.currency || undefined}
                              />
                        {summaryVatRate > 0 && price.DeliveryInsurance > 0 &&
                          <div>
                            <span className="withoutVat">
                              <Currency price={price.DeliveryInsurance} currency={order && order.currency || undefined} /> incl. {vatDescription()}
                            </span>
                          </div>}
                      </div>
                    </div>
                  </div>}
              </div>
            </div>
          )}
        </FilterableAttributes>
  
        <div className="orderBox__infoItem orderBox__infoItem--final">
          <div className="orderBox__price">
            <span className="orderBox__label">Total price:</span>
            <Currency price={totalPriceWithoutVat} decimals={2} currency={currency} />

            {summaryVatRate > 0 &&
              <div className="cart__totalPrice withoutVat">
                Total price {VatService.GetInstance().displayIncludeVat(summaryVatRate, summaryCountry)} :{' '}
                <Currency price={totalPriceWithVat} freeAsWord={false} currency={currency} />
              </div>}
          </div>
        </div>
      </div>
    );
  }
}

export default OrderBox;



// WEBPACK FOOTER //
// ./src/modules/OrderBox/OrderBox.tsx